import React, { useState } from 'react';
import { Runbook } from '../types';
import confetti from 'canvas-confetti';

interface CheckoutModalProps {
  runbook: Runbook | null;
  isOpen: boolean;
  onClose: () => void;
  onCompletePurchase: (email: string) => void;
  stripePaymentLink?: string;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  runbook,
  isOpen,
  onClose,
  onCompletePurchase,
  stripePaymentLink,
}) => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !runbook) return null;

  const handlePay = () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      alert('A valid email is required to register your runbook access signature.');
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      // Fire celebratory confetti
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#38bdf8', '#10b981', '#f59e0b']
      });

      onCompletePurchase(trimmed);
      setIsProcessing(false);
      onClose();
    }, 700);
  };

  const handleExternalStripe = () => {
    if (stripePaymentLink && stripePaymentLink.startsWith('http')) {
      window.open(stripePaymentLink, '_blank');
    } else {
      alert('To test with a live Stripe Payment Link, set the URL in the Terminal settings. For this session, Complete Payment performs instant direct in-page unlock.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl blueprint-card p-6 space-y-6 relative border border-sky-500/40">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#8b92a5] hover:text-white font-mono text-lg"
        >
          &times;
        </button>

        <div>
          <div className="text-[10px] font-mono text-sky-400 uppercase tracking-widest">
            SECURE CHECKOUT // IN-PAGE UNLOCK
          </div>
          <h3 className="font-tech text-2xl font-bold text-white mt-1">
            {runbook.title}
          </h3>
          <p className="font-mono text-2xl font-bold text-sky-400 mt-1">
            ${runbook.price}.00 USD
          </p>
        </div>

        <div className="space-y-4 font-mono">
          <div className="space-y-1">
            <label className="text-[11px] uppercase text-[#8b92a5]">
              Recipient Email (For Unlock Signature)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@firm.com"
              className="w-full px-3 py-2.5 rounded bg-[#181b26] border border-[#232736] text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-sky-400"
            />
          </div>

          <div className="p-3 rounded bg-[#181b26] border border-[#232736] text-[11px] text-[#8b92a5] space-y-1">
            <div className="text-white font-bold flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Instant Direct Delivery
            </div>
            <p>Payment confirmation unlocks the full operational runbook right here on this screen.</p>
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={handlePay}
              disabled={isProcessing}
              className="w-full py-3 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-xs rounded transition shadow-[0_0_15px_rgba(56,189,248,0.2)] disabled:opacity-50"
            >
              {isProcessing ? 'COMMITTING TRANSACTION VIA STRIPE...' : 'COMPLETE PAYMENT & UNLOCK'}
            </button>
            <button
              onClick={handleExternalStripe}
              className="w-full py-2 border border-[#232736] text-zinc-400 hover:text-white text-[11px] rounded transition"
            >
              Use External Stripe Link &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
